/**
 * Module-level navigation configuration.
 *
 * Single typed source of truth for two-level navigation (sidebar module
 * consolidation + horizontal top submenu). Each entry in `navigationModules`
 * produces a consolidated sidebar link + a horizontal submenu bar.
 *
 * Sections whose label does not appear in `navigationModules` (Platform,
 * Settings) remain flat in the sidebar — no submenu is shown for them.
 *
 * =============================================================================
 * SCOPE MODEL (ADR-0024)
 * =============================================================================
 *
 * Every nav item carries an optional `scope` field:
 *   'platform' — visible only to platform-scope admins (Me.is_platform_admin).
 *                system:admin does NOT bypass this gate.
 *   'tenant'   — visible to tenant-scope users; standard system:admin wildcard applies.
 *   'both'     — visible to both scopes (default when omitted).
 *
 * `platformAdminOnly: true` is a deprecated alias for `scope:'platform'` kept
 * for backward compatibility. New items MUST use `scope:'platform'` instead.
 *
 * `featureFlag?: FeatureFlagKey` — when set, the item is additionally gated by
 * the flag value. canSeeNavItem/navItemPasses in permissions.ts handle this.
 * The pure predicates do not import the hook; callers AND the flag result.
 *
 * =============================================================================
 * SCOPE CLASSIFICATION RATIONALE
 * =============================================================================
 *
 * Rule: if tenant_admin's permission set (migration 0288) INCLUDES the gating
 * permission → scope:'tenant'. If the surface is gated by a platform-only
 * permission (tenant_admin lacks it) → scope:'platform'.
 *
 * AMBIGUOUS items (filed for product approval — see section below):
 *   - Scenarios / Rules: currently system:admin; tenant-tunable? (filed)
 *   - Bank Configuration: currently system:admin; tenant-configurable? (filed)
 *   - Challenge Plane Settings: currently system:admin; tenant setting? (filed)
 *   - Scheduled Jobs: currently system:admin; tenant-runnable? (filed)
 *   - Webhooks: tenant_admin lacks webhooks:write/approve → platform (filed)
 *   - API Keys: tenant_admin lacks api_keys:* → platform (filed)
 *   - Maker-Checker / Fraud→AML / Communications: system:admin only (filed)
 *   - Executive Dashboard: system:admin only (filed)
 *   - Settings hub: system:admin only (filed)
 *   For all ambiguous items, defaulting to scope:'platform' (fail-closed).
 */
import type { ComponentType } from 'react'
import { matchPath } from 'react-router-dom'
import type { FeatureFlagKey } from '@/features/databus/api-feature-flags'

/** Lucide icon component shape (minimal intersection used by the sidebar). */
type IconComponent = ComponentType<{ size?: number; className?: string }>

/**
 * Scope classification for a nav item.
 *   'platform' — requires Me.is_platform_admin. No system:admin bypass.
 *   'tenant'   — standard permission gate; system:admin wildcard applies.
 *   'both'     — visible to both (default if omitted).
 */
export type NavScope = 'platform' | 'tenant' | 'both'

export interface ModuleNavItem {
  label: string
  /** react-router path string, matched with matchPath so sub-routes stay highlighted. */
  path: string
  /** Permissions required — semantics match useCanAny: visible if user holds ANY listed perm.
   *  Omit / empty array = visible to all authenticated users (within the scope). */
  permissions?: readonly string[]
  /**
   * Scope gate. Controls which class of user can see this item.
   *   'platform' — requires Me.is_platform_admin (no system:admin bypass).
   *   'tenant'   — standard permission gate (system:admin wildcard applies).
   *   'both'     — visible to both (default when omitted).
   *
   * This is the canonical field. Use this on all new items.
   */
  scope?: NavScope
  /**
   * @deprecated Use `scope: 'platform'` instead.
   *
   * Legacy alias for `scope: 'platform'`. Kept for back-compat while the
   * migration from platformAdminOnly → scope is in progress. The central
   * predicate (canSeeNavItem) treats `platformAdminOnly: true` identically
   * to `scope: 'platform'`.
   */
  platformAdminOnly?: boolean
  /**
   * Optional feature-flag key. When set, the item is hidden unless the
   * named flag is enabled. Callers AND this with canSeeNavItem via navItemPasses.
   */
  featureFlag?: FeatureFlagKey
  /**
   * When true, the item only activates on an exact path match (end: true).
   * Default is prefix matching (end: false) so that detail routes like /alerts/:id
   * keep the parent item highlighted.
   *
   * Set exact: true on items whose path is a strict prefix of an unrelated
   * route (e.g. /admin/challenges is a prefix of /admin/challenges/settings
   * which lives in Administration, not Investigations).
   */
  exact?: boolean
}

export interface NavigationModule {
  key: string
  label: string
  /**
   * Optional override for the SECTIONS label used to match this module to
   * its sidebar section. When absent, `label` is used for both display AND
   * section-to-module matching (the common case — SECTIONS label === module label).
   *
   * Use this when the SECTIONS label differs from the desired display label.
   * Example: the 'tenant-admin' module displays as "Administration" (label) but
   * its SECTIONS entry is labeled "Tenant Admin" (sectionLabel). The moduleByLabel
   * Map in Sidebar.tsx should key on sectionLabel when set.
   */
  sectionLabel?: string
  /** Icon rendered in the consolidated sidebar entry. */
  Icon: IconComponent
  /** Default path navigated to when the user clicks the consolidated sidebar item. */
  defaultPath: string
  items: readonly ModuleNavItem[]
}

/**
 * Returns true when `pathname` matches the given module item path.
 *
 * By default uses prefix matching (end: false) so that nested detail
 * routes like `/alerts/:id` or `/cases/:id/timeline` keep the correct
 * item highlighted.
 *
 * Pass `exact: true` for items whose path is a strict prefix of an
 * unrelated sibling route (e.g. /admin/challenges vs
 * /admin/challenges/settings) to avoid false-positive active states.
 */
export function isItemActive(
  itemPath: string,
  pathname: string,
  exact = false,
): boolean {
  return matchPath({ path: itemPath, end: exact }, pathname) !== null
}

/**
 * Returns the first module whose items include a path matching `pathname`.
 * Honors each item's `exact` flag so that prefix collisions between module
 * items and unrelated admin routes don't produce false positives.
 * Returns `undefined` for non-module routes (e.g. `/settings`, `/login`).
 */
export function findActiveModule(pathname: string): NavigationModule | undefined {
  return navigationModules.find((mod) =>
    mod.items.some((item) => isItemActive(item.path, pathname, item.exact)),
  )
}

/**
 * Returns the path of the first module item the current user can see,
 * falling back to `module.defaultPath` if no item passes the permission
 * check (should not happen in practice — the consolidated sidebar entry
 * is only rendered when at least one item is visible).
 *
 * Uses the same permission semantics as canSeeNavItem (permissions.ts):
 *   - scope:'platform' → only visible if isPlatformAdmin is true (no system:admin bypass)
 *   - system:admin in userPerms → short-circuit for non-platform-scope items
 *   - otherwise → visible if userPerms contains ANY listed permission
 *
 * @param isPlatformAdmin - pass `me?.is_platform_admin === true`; do NOT derive
 *   from `userPerms.includes('platform:admin')` — that string is not a real
 *   backend permission (ADR-0023).
 */
export function firstVisibleItemPath(
  module: NavigationModule,
  userPerms: readonly string[],
  isPlatformAdmin: boolean,
): string {
  const isSuperAdmin = userPerms.includes('system:admin')
  for (const item of module.items) {
    // Determine effective scope (scope field takes precedence; platformAdminOnly is a legacy alias).
    const scope = item.scope ?? (item.platformAdminOnly ? 'platform' : 'both')

    // Platform-scope items require is_platform_admin — system:admin alone is not enough.
    if (scope === 'platform') {
      if (isPlatformAdmin) return item.path
      continue
    }

    if (!item.permissions || item.permissions.length === 0) return item.path
    if (isSuperAdmin) return item.path
    if (item.permissions.some((p) => userPerms.includes(p))) return item.path
  }
  return module.defaultPath
}

// ---------------------------------------------------------------------------
// Icon imports — used only in navigationModules below.
// We import them here rather than in Sidebar.tsx so the module registry is
// self-contained and the sidebar no longer hardcodes per-module icons.
// ---------------------------------------------------------------------------
import {
  UserCircle,
  Briefcase,
  ShieldCheck,
  Activity,
  Newspaper,
  LayoutDashboard,
  Brain,
  UserCog,
  Database,
  HeartPulse,
  Settings,
} from 'lucide-react'

/**
 * All configured navigation modules, in sidebar display order.
 *
 * Each entry here causes:
 *   1. The matching Sidebar section (by label) to render as a single
 *      consolidated NavLink instead of its flat items.
 *   2. A horizontal ModuleTopNav bar to appear for any route owned by
 *      the module.
 *
 * Sections whose label does NOT appear here (Platform, Settings) stay flat.
 *
 * Required exact:true items (prevents same-module prefix bleed):
 *   /admin/challenges (Investigations) — prefix of /admin/challenges/settings (Administration)
 *   /blocks          (Compliance)      — prefix of /blocks/approvals (special conditional link)
 *   /dashboard       (Dashboards)      — prefix of /dashboard/hq (same module, different item)
 *   /ml              (Detection)       — prefix of /ml/monitoring, /ml/governance (same module)
 *   /admin/databus   (Ingestion)       — prefix of /admin/databus/* (same module, different items)
 *   /workflow        (Operations)      — prefix of /workflow/state-machine (same module)
 *
 * Scope classification:
 *   scope:'platform' — item requires Me.is_platform_admin; system:admin does NOT bypass.
 *   scope:'tenant'   — item is accessible to tenant-scope users with the listed permission.
 *   omitted/'both'   — accessible to all scopes with permission.
 */
export const navigationModules: readonly NavigationModule[] = [
  {
    key: 'home',
    label: 'Home',
    Icon: UserCircle,
    defaultPath: '/my-dashboard',
    items: [
      // My Dashboard: no permission required — every authenticated user has a home.
      { label: 'My Dashboard', path: '/my-dashboard', scope: 'both' },
      // Inbox: tenant_admin has inbox:read — tenant scope.
      { label: 'Inbox', path: '/inbox', permissions: ['inbox:read'], scope: 'tenant' },
    ],
  },
  {
    key: 'investigations',
    label: 'Investigations',
    Icon: Briefcase,
    defaultPath: '/alerts',
    items: [
      // tenant_admin has cases:read — tenant scope.
      { label: 'Alerts', path: '/alerts', permissions: ['cases:read'], scope: 'tenant' },
      { label: 'Cases', path: '/cases', permissions: ['cases:read'], scope: 'tenant' },
      {
        label: 'Customer Challenges',
        path: '/admin/challenges',
        // Backend canonical view permission: audit:read (challenges_admin_handler.go).
        // analyst, compliance_officer, and viewer all hold audit:read; this is the
        // operational challenge feed. tenant_admin has audit:read — tenant scope.
        permissions: ['audit:read'],
        scope: 'tenant',
        // exact: true prevents /admin/challenges/settings (Administration →
        // Challenge Plane Settings) from false-positive matching this item.
        exact: true,
      },
      // tenant_admin has customers:read — tenant scope.
      { label: 'Customers', path: '/customers', permissions: ['customers:read'], scope: 'tenant' },
      { label: 'Transactions', path: '/transactions', permissions: ['transactions:read'], scope: 'tenant' },
      {
        label: 'Network Explorer',
        path: '/investigations/network',
        permissions: ['cases:read', 'customers:read'],
        scope: 'tenant',
      },
      {
        label: 'Consortium Intel',
        path: '/intelligence/consortium',
        permissions: ['consortium:read'],
        scope: 'tenant',
      },
    ],
  },
  {
    key: 'compliance',
    label: 'Compliance',
    Icon: ShieldCheck,
    defaultPath: '/compliance',
    items: [
      // tenant_admin has audit:read — tenant scope.
      { label: 'Compliance Dashboard', path: '/compliance', permissions: ['audit:read'], scope: 'tenant' },
      // tenant_admin has sars:read — tenant scope.
      { label: 'SAR / STR', path: '/sars', permissions: ['sars:read'], scope: 'tenant' },
      {
        label: 'Blocks',
        path: '/blocks',
        permissions: ['blocks:read'],
        scope: 'tenant',
        // exact: true so /blocks/approvals (the special canApprove conditional link)
        // does not false-positive activate the "Blocks" item on the top submenu.
        exact: true,
      },
      { label: 'Reports', path: '/reports', permissions: ['reports:read'], scope: 'tenant' },
      { label: 'Handovers', path: '/handovers', permissions: ['handovers:read'], scope: 'tenant' },
    ],
  },
  {
    key: 'sanctions-screening',
    label: 'Sanctions & Screening',
    Icon: Activity,
    defaultPath: '/screening/dashboard',
    items: [
      // tenant_admin has screening:read — tenant scope.
      { label: 'Dashboard', path: '/screening/dashboard', permissions: ['screening:read'], scope: 'tenant' },
      {
        label: 'Console',
        path: '/screening/console',
        // ADR-0023: the Screening Console is a platform-level engine-management surface
        // (source catalogue, cadence control, engine status). Tenant users access
        // their tenant-scoped results via Dashboard and History only. The Console
        // is restricted to platform admins; system:admin bypass is intentionally OFF.
        scope: 'platform',
      },
      { label: 'History', path: '/screening/history', permissions: ['screening:read'], scope: 'tenant' },
    ],
  },
  {
    key: 'adverse-media',
    label: 'Adverse Media',
    Icon: Newspaper,
    defaultPath: '/adverse-media/dashboard',
    items: [
      // ADVERSE MEDIA SCOPE DECISION (Phase 4 — ADR-0024):
      //
      // Tenant-data surfaces (Dashboard, Feeds, Classifications, Profiles, Eval set):
      //   → scope:'tenant'
      //
      // Rationale:
      //   1. The backend schema (migration 0292) stores adverse_media profiles and
      //      classifications in tenant-private tables (adverse_media_profiles,
      //      adverse_media_classifications). These are NOT cross-tenant shared data.
      //   2. adverse_media:read is NOT in the tenant_admin 48-perm default grant set
      //      per the ADR-0024 audit. This means tenant admins who want adverse-media
      //      access receive the permission explicitly — it is not a default entitlement.
      //      However, the scope:'tenant' classification is correct regardless: the
      //      data is tenant-private and should be accessed in a tenant context.
      //   3. A platform admin in PLATFORM mode (no tenant selected) would call
      //      tenant-scoped adverse-media APIs without a tenant context → backend 403
      //      or cross-tenant data leak. scope:'tenant' ensures these items are only
      //      shown to: (a) tenant users with adverse_media:read, or (b) platform
      //      admins in TENANT-VIEW mode (selectedTenantId != null) who also hold
      //      the permission (or system:admin which short-circuits).
      //
      // Prompts surface (/adverse-media/prompts):
      //   → scope:'platform'
      //
      // Rationale:
      //   LLM-prompt management for adverse-media screening is a platform-level
      //   configuration surface (shared prompt templates used across tenants).
      //   The gating permission adverse_media:prompts_admin is not a tenant
      //   permission. Platform admins manage prompts; tenant users only consume
      //   adverse-media results via the tenant-data surfaces above.
      {
        label: 'Dashboard',
        path: '/adverse-media/dashboard',
        permissions: ['adverse_media:read'],
        scope: 'tenant',
      },
      { label: 'Feeds', path: '/adverse-media/feeds', permissions: ['adverse_media:read'], scope: 'tenant' },
      {
        label: 'Classifications',
        path: '/adverse-media/classifications',
        permissions: ['adverse_media:read'],
        scope: 'tenant',
      },
      {
        label: 'Profiles',
        path: '/adverse-media/profiles',
        permissions: ['adverse_media:read'],
        scope: 'tenant',
      },
      {
        label: 'Eval set',
        path: '/adverse-media/eval-set',
        permissions: ['adverse_media:read'],
        scope: 'tenant',
      },
      {
        label: 'Prompts',
        path: '/adverse-media/prompts',
        permissions: ['adverse_media:prompts_admin'],
        // Platform-level LLM prompt management: shared templates across tenants.
        // Tenant users only consume adverse-media results; they do not configure prompts.
        scope: 'platform',
      },
    ],
  },
  {
    key: 'dashboards',
    label: 'Dashboards',
    Icon: LayoutDashboard,
    defaultPath: '/dashboard',
    items: [
      {
        label: 'Executive Dashboard',
        path: '/dashboard',
        // AMBIGUOUS: executive dashboard is system:admin only. Is it platform-only
        // or tenant-admin accessible? Defaulting to scope:'platform' (fail-closed).
        // Filed for product approval.
        permissions: ['system:admin'],
        scope: 'platform',
        // exact: true so /dashboard/hq does not false-activate Executive Dashboard.
        exact: true,
      },
      // tenant_admin has analytics:read — tenant scope.
      { label: 'Trend Analytics', path: '/analytics', permissions: ['audit:read'], scope: 'tenant' },
      { label: 'Operations Health', path: '/operations', permissions: ['audit:read'], scope: 'tenant' },
      { label: 'Risk Heatmap', path: '/highlights', permissions: ['audit_log:read'], scope: 'tenant' },
      { label: 'HQ Rollup', path: '/dashboard/hq', permissions: ['audit:read'], scope: 'tenant' },
      { label: 'Role Workspaces', path: '/role-dashboards', permissions: ['audit:read'], scope: 'tenant' },
      { label: 'All Dashboards', path: '/portfolio', permissions: ['audit:read'], scope: 'tenant' },
    ],
  },
  {
    key: 'detection',
    label: 'Detection',
    Icon: Brain,
    // defaultPath: tenant-admins land on /tenant-admin/scenarios (first visible item);
    // platform-admins land on /admin/scenarios (also first visible for them).
    defaultPath: '/admin/scenarios',
    items: [
      {
        label: 'Scenarios',
        path: '/admin/scenarios',
        // Platform cross-tenant scenario management. system:admin required;
        // tenant-admin self-service uses /tenant-admin/scenarios below.
        permissions: ['system:admin'],
        scope: 'platform',
      },
      {
        label: 'Rules',
        path: '/admin/rules',
        // Platform cross-tenant rule management.
        permissions: ['system:admin'],
        scope: 'platform',
      },
      // ADR-0023 Track 4 — Tenant self-service scenario + rule management.
      // Visible to tenant-scope users with scenarios:read. Mutually exclusive
      // with the platform entries above via scope gating (platform users see
      // /admin/scenarios, not /tenant-admin/scenarios).
      {
        label: 'My Scenarios',
        path: '/tenant-admin/scenarios',
        permissions: ['scenarios:read'],
        scope: 'tenant',
      },
      {
        label: 'My Rules',
        path: '/tenant-admin/rules',
        permissions: ['scenarios:read'],
        scope: 'tenant',
      },
      {
        label: 'Model Registry',
        path: '/ml',
        // tenant_admin has ml:read — tenant scope.
        permissions: ['ml:read'],
        scope: 'tenant',
        // exact: true so /ml/monitoring and /ml/governance don't double-activate
        // both Model Registry and their own item.
        exact: true,
      },
      { label: 'Model Monitoring', path: '/ml/monitoring', permissions: ['ml:read'], scope: 'tenant' },
      { label: 'Explainable AI', path: '/ml/governance', permissions: ['ml:read'], scope: 'tenant' },
      // tenant_admin has ml_feedback:read — tenant scope.
      { label: 'Closed-Loop Feedback', path: '/admin/ml', permissions: ['ml_feedback:read'], scope: 'tenant' },
      {
        label: 'Maker-Checker',
        path: '/admin/maker-checker',
        // AMBIGUOUS: system:admin only; tenant-level workflow config? Filed for product approval.
        // Defaulting to scope:'platform' (fail-closed).
        permissions: ['system:admin'],
        scope: 'platform',
      },
      {
        label: 'Fraud → AML',
        path: '/admin/fraud-to-aml',
        // AMBIGUOUS: system:admin only. Filed for product approval.
        // Defaulting to scope:'platform' (fail-closed).
        permissions: ['system:admin'],
        scope: 'platform',
      },
      {
        label: 'Communications',
        path: '/admin/communications',
        // AMBIGUOUS: system:admin only. Filed for product approval.
        // Defaulting to scope:'platform' (fail-closed).
        permissions: ['system:admin'],
        scope: 'platform',
      },
    ],
  },
  {
    key: 'administration',
    label: 'Administration',
    Icon: UserCog,
    // S2 fix (ADR-0024): defaultPath was /admin/users (scope:'platform').
    // Changed to /admin/branches (scope:'tenant') so tenant users who land
    // on the Administration module have a safe fallback. Platform admins
    // still see /admin/users first via firstVisibleItemPath (it is the first
    // item in the array and is scope:'platform' — visible to platform admins).
    defaultPath: '/admin/branches',
    items: [
      {
        label: 'Users & RBAC',
        path: '/admin/users',
        permissions: ['system:admin'],
        // ADR-0023: platform-admin-only. Tenant-admins hold system:admin for their
        // tenant but the backend /admin/users now requires actor_scope_type=platform.
        // scope:'platform' bypasses the system:admin short-circuit.
        scope: 'platform',
      },
      {
        label: 'Role Tiers',
        path: '/admin/roles',
        permissions: ['system:admin'],
        // ADR-0023: global RBAC catalogue is platform-admin-only.
        scope: 'platform',
      },
      // ADR-0026 Phase B: /admin/branches and /admin/teams are NOW platform-only.
      // Tenant admins self-manage via /tenant-admin/branches and /tenant-admin/teams.
      // The platform admin uses these legacy pages (with TenantSelectorBand) to inspect
      // any tenant's branches/teams while in tenant-view mode.
      { label: 'Branches', path: '/admin/branches', permissions: ['branches:read'], scope: 'platform' },
      { label: 'Teams', path: '/admin/teams', permissions: ['teams:read'], scope: 'platform' },
      {
        label: 'Access Requests',
        path: '/access-requests',
        // access:grant is platform-scoped (tenant_admin lacks it).
        // system:admin is platform-scoped too. Failing closed → platform.
        permissions: ['access:grant', 'system:admin'],
        scope: 'platform',
      },
      {
        label: 'Bank Configuration',
        path: '/admin/bank-configuration',
        // AMBIGUOUS: system:admin only; per-tenant configurable? Filed for product approval.
        // Defaulting to scope:'platform' (fail-closed).
        permissions: ['system:admin'],
        scope: 'platform',
      },
      {
        label: 'Scheduled Jobs',
        path: '/admin/jobs',
        // AMBIGUOUS: system:admin only. Filed for product approval.
        // Defaulting to scope:'platform' (fail-closed).
        permissions: ['system:admin'],
        scope: 'platform',
      },
      {
        label: 'Challenge Plane Settings',
        path: '/admin/challenges/settings',
        // AMBIGUOUS: system:admin only; tenant setting? Filed for product approval.
        // Defaulting to scope:'platform' (fail-closed).
        permissions: ['system:admin'],
        scope: 'platform',
      },
      {
        label: 'API Keys',
        path: '/admin/api-keys',
        // tenant_admin lacks api_keys:* → platform scope.
        permissions: ['system:admin'],
        scope: 'platform',
      },
      {
        label: 'Webhooks',
        path: '/admin/webhooks',
        // tenant_admin lacks webhooks:write/approve → platform scope.
        permissions: ['system:admin'],
        scope: 'platform',
      },
      {
        label: 'Observability ACL',
        path: '/admin/observability/access-control',
        // tenant_admin has observability:read — tenant scope.
        // The page enforces granular propose/approve/emergency affordances internally.
        permissions: ['observability:read', 'system:admin'],
        scope: 'tenant',
      },
      {
        label: 'Integrations',
        path: '/admin/integrations',
        // ADR-0023: the integrations catalogue (42 vendor rows: Dojah, OFAC, NIBSS, etc.)
        // is a platform-level vendor registry. Tenant users must not see or configure it.
        // system:admin bypass is intentionally OFF — scope:'platform' handles this.
        scope: 'platform',
      },
      {
        label: 'Platform Feature Flags',
        path: '/admin/platform/feature-flags',
        // ADR-0023: platform-level feature gates (e.g. multi_tenant_enabled).
        // Platform admin only; system:admin bypass is intentionally OFF.
        scope: 'platform',
      },
    ],
  },
  {
    key: 'ingestion',
    label: 'Ingestion',
    Icon: Database,
    defaultPath: '/admin/databus/tenants',
    items: [
      // All databus:* permissions are platform-scoped (tenant_admin lacks them).
      {
        label: 'Tenant Management',
        path: '/admin/databus/tenants',
        permissions: ['databus:admin'],
        scope: 'platform',
      },
      {
        label: 'Add Connector',
        path: '/admin/databus/ingestion-channels/new',
        permissions: ['databus:admin'],
        scope: 'platform',
      },
      {
        label: 'Ops Dashboard',
        path: '/admin/databus',
        permissions: ['databus:read'],
        scope: 'platform',
        // exact: true so /admin/databus/adapters etc. don't double-activate
        // both Ops Dashboard and their own item.
        exact: true,
      },
      {
        label: 'Tenants & Adapters',
        path: '/admin/databus/adapters',
        permissions: ['databus:read'],
        scope: 'platform',
      },
      {
        label: 'Mapping Editor',
        path: '/admin/databus/mappings',
        permissions: ['databus:read'],
        scope: 'platform',
      },
      {
        label: 'Schema Registry',
        path: '/admin/databus/schemas',
        permissions: ['databus:read'],
        scope: 'platform',
      },
      {
        label: 'Dead-Letter Queue',
        path: '/admin/databus/dlq',
        permissions: ['databus:read'],
        scope: 'platform',
      },
      {
        label: 'Reconciliation',
        path: '/admin/databus/recon',
        permissions: ['databus:read'],
        scope: 'platform',
      },
      {
        label: 'Lineage Explorer',
        path: '/admin/databus/lineage',
        permissions: ['databus:read'],
        scope: 'platform',
      },
      {
        label: 'Databus Settings',
        path: '/admin/databus/settings',
        permissions: ['databus:read'],
        scope: 'platform',
      },
      {
        label: 'Runs',
        path: '/admin/databus-simulator',
        permissions: ['databus:admin'],
        scope: 'platform',
      },
      {
        label: 'Scenarios',
        path: '/admin/databus-simulator/scenarios',
        permissions: ['databus:admin'],
        scope: 'platform',
      },
      {
        label: 'Chaos drill',
        path: '/admin/databus-simulator/chaos',
        permissions: ['databus:admin'],
        scope: 'platform',
      },
      {
        label: 'Allowlist',
        path: '/admin/databus-simulator/allowlist',
        permissions: ['databus:admin'],
        scope: 'platform',
      },
      {
        label: 'Sim services',
        path: '/admin/databus-simulator/system',
        permissions: ['databus:admin'],
        scope: 'platform',
      },
    ],
  },
  {
    key: 'operations',
    label: 'Operations',
    Icon: HeartPulse,
    defaultPath: '/admin/system-status',
    items: [
      // system:read is platform-scoped (tenant_admin lacks it).
      {
        label: 'System Status',
        path: '/admin/system-status',
        permissions: ['system:read'],
        scope: 'platform',
      },
      {
        label: 'Services Control',
        path: '/admin/services',
        permissions: ['system:admin'],
        scope: 'platform',
      },
      {
        label: 'Ingestion Engine',
        path: '/admin/ingestion',
        permissions: ['system:read'],
        scope: 'platform',
      },
      {
        label: 'Batch Processing',
        path: '/admin/batch',
        permissions: ['system:read'],
        scope: 'platform',
      },
      // outbox:* is platform-scoped (tenant_admin lacks it).
      {
        label: 'Outbox Replay',
        path: '/admin/outbox-replay',
        permissions: ['outbox:request', 'outbox:approve'],
        scope: 'platform',
      },
      // tenant_admin has workflow:read — tenant scope.
      {
        label: 'Workflow Queues',
        path: '/workflow',
        permissions: ['workflow:read'],
        scope: 'tenant',
        // exact: true so /workflow/state-machine does not double-activate
        // both Workflow Queues and State Machine.
        exact: true,
      },
      {
        label: 'State Machine',
        path: '/workflow/state-machine',
        permissions: ['workflow:read'],
        scope: 'tenant',
      },
      // tenant_admin has audit_log:read — tenant scope.
      { label: 'Audit Log', path: '/admin/audit', permissions: ['audit_log:read'], scope: 'tenant' },
      // access_log:read is platform-scoped (tenant_admin lacks it).
      { label: 'Access Logs', path: '/admin/access-log', permissions: ['access_log:read'], scope: 'platform' },
      // demo:admin is platform-scoped (tenant_admin lacks it).
      {
        label: 'Demo Data',
        path: '/admin/demo-data',
        permissions: ['demo:admin', 'system:admin'],
        scope: 'platform',
      },
    ],
  },
  // ---------------------------------------------------------------------------
  // Tenant Administration module (ADR-0026 Phase C)
  //
  // Tenant self-service administration — Users & Roles, Roles, Approvals,
  // Branches, Teams. All items are scope:'tenant'; the platform "Administration"
  // module (key:'administration', /admin/*) handles the platform-scope equivalents.
  //
  // LABEL COLLISION RESOLUTION:
  //   - This module displays as "Administration" (matching the platform module)
  //     but the SECTIONS entry that maps to it uses label:'Tenant Admin'.
  //   - moduleByLabel in Sidebar.tsx is keyed by SECTION label ('Tenant Admin'),
  //     NOT by module display label. 'Tenant Admin' → this module; 'Administration'
  //     → the platform module. No collision.
  //   - A tenant user (is_platform_admin:false) never sees the platform
  //     Administration module (all items scope:'platform' or scope:'tenant' guarded
  //     by platform permissions). A platform admin in platform mode never sees
  //     this module (all items scope:'tenant', canSeeNavItem returns false in
  //     platform mode). A platform admin in tenant-view mode sees this module
  //     alongside the platform module — that is correct and expected.
  //
  // SECTION ENTRY: SECTIONS['Tenant Admin'] in sidebar-sections.ts.
  // ROUTE GUARD: RequireTenantScopedRoute wraps all /tenant-admin/* routes.
  // ---------------------------------------------------------------------------
  {
    key: 'tenant-admin',
    label: 'Administration',
    // sectionLabel resolves the SECTIONS lookup: the SECTIONS entry for this module
    // is labeled 'Tenant Admin' (not 'Administration') to avoid collision with the
    // platform 'Administration' module section. Sidebar.tsx keys moduleByLabel on
    // sectionLabel (when present) instead of label.
    sectionLabel: 'Tenant Admin',
    Icon: Settings,
    defaultPath: '/tenant-admin/users',
    items: [
      {
        label: 'Users & Roles',
        path: '/tenant-admin/users',
        permissions: ['tenant_users:admin'],
        scope: 'tenant',
      },
      {
        // ADR-0025: Tenant-scoped role management.
        label: 'Roles',
        path: '/tenant-admin/roles',
        permissions: ['tenant_roles:read'],
        scope: 'tenant',
      },
      {
        // ADR-0025/ADR-0026: Tenant-internal maker-checker approvals inbox.
        // OR semantics: visible if user holds any of the three approve perms.
        label: 'Approvals',
        path: '/tenant-admin/approvals',
        permissions: ['tenant_roles:approve', 'tenant_branches:approve', 'tenant_teams:approve'],
        scope: 'tenant',
      },
      {
        // ADR-0026 Phase B: Tenant self-service branch management.
        label: 'Branches',
        path: '/tenant-admin/branches',
        permissions: ['tenant_branches:read'],
        scope: 'tenant',
      },
      {
        // ADR-0026 Phase B: Tenant self-service team management.
        label: 'Teams',
        path: '/tenant-admin/teams',
        permissions: ['tenant_teams:read'],
        scope: 'tenant',
      },
    ],
  },
]
